import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Spin, Switch, Tag, Tooltip, Input, Button, Tabs } from 'antd';
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { FiUsers, FiTrash2, FiLayout, FiBox } from 'react-icons/fi';
import api from '../services/api';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useSidebar } from '../contexts/SidebarContext';
import { invalidateSettingsCache } from '../hooks/useSystemSettings';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';
import SmtpSettingsTab from '../components/system-settings/SmtpSettingsTab';
import FreeIpaSettingsTab from '../components/system-settings/FreeIpaSettingsTab';

interface Setting {
    value: string;
    description?: string;
    updatedBy?: string;
    updatedAt: string;
}
type SettingsMap = Record<string, Setting>;

interface SettingCardProps {
    label: string;
    settingKey: string;
    settings: SettingsMap;
    isSuperadmin: boolean;
    type: 'toggle' | 'number' | 'text';
    unit?: string;
    disabledReason?: string;
    onSave: (key: string, value: string) => Promise<void>;
}

function SettingCard({ label, settingKey, settings, isSuperadmin, type, unit, disabledReason, onSave }: SettingCardProps) {
    const useNew = useNewGui();
    const setting = settings[settingKey];
    const [localValue, setLocalValue] = useState(setting?.value ?? '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setLocalValue(setting?.value ?? ''); }, [setting?.value]);

    const handleToggle = async (checked: boolean) => {
        setSaving(true);
        await onSave(settingKey, checked ? 'true' : 'false');
        setSaving(false);
    };

    const handleNumberSave = async () => {
        setSaving(true);
        await onSave(settingKey, localValue);
        setSaving(false);
    };

    const lastUpdated = setting?.updatedAt
        ? `Last updated ${new Date(setting.updatedAt).toLocaleString()}${setting.updatedBy ? ` by ${setting.updatedBy}` : ''}`
        : '';

    return (
        <div className={useNew ? "flex items-start justify-between py-4 border-b border-border-subtle last:border-0" : "flex items-start justify-between py-4 border-b border-gray-100 last:border-0"}>
            <div className="flex-1 pr-6">
                <div className="flex items-center gap-2 mb-1">
                    <span className={useNew ? "font-medium text-ink-primary" : "font-medium text-gray-800"}>{label}</span>
                    {setting?.description && (
                        <Tooltip title={setting.description}>
                            <InfoCircleOutlined className={useNew ? "text-ink-secondary cursor-help" : "text-gray-400 cursor-help"} />
                        </Tooltip>
                    )}
                </div>
                {lastUpdated && (
                    <span className={useNew ? "text-xs text-ink-secondary" : "text-xs text-gray-400"}>{lastUpdated}</span>
                )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                {type === 'toggle' ? (
                    <>
                        <Tag color={localValue === 'true' ? 'green' : 'default'}>
                            {localValue === 'true' ? 'ON' : 'OFF'}
                        </Tag>
                        <Tooltip title={disabledReason}>
                            <Switch
                                checked={localValue === 'true'}
                                onChange={handleToggle}
                                loading={saving}
                                disabled={!isSuperadmin || !!disabledReason}
                            />
                        </Tooltip>
                    </>
                ) : type === 'text' ? (
                    <div className="flex items-center gap-2">
                        <Input
                            value={localValue}
                            onChange={e => setLocalValue(e.target.value)}
                            style={{ width: 260 }}
                            disabled={!isSuperadmin || !!disabledReason}
                        />
                        {isSuperadmin && !disabledReason && (
                            <Button
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={handleNumberSave}
                                loading={saving}
                                disabled={localValue === setting?.value}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            value={localValue}
                            onChange={e => setLocalValue(e.target.value)}
                            style={{ width: 80 }}
                            disabled={!isSuperadmin || !!disabledReason}
                            suffix={unit}
                            min={1}
                        />
                        {isSuperadmin && !disabledReason && (
                            <Button
                                size="small"
                                icon={<SaveOutlined />}
                                onClick={handleNumberSave}
                                loading={saving}
                                disabled={localValue === setting?.value}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Stable section chrome — must stay outside render so children are not remounted. */
const FeatureSection: React.FC<{
    tone: 'accent' | 'warning' | 'info';
    icon: React.ReactNode;
    title: string;
    desc: React.ReactNode;
    children: React.ReactNode;
    useNew: boolean;
}> = ({ tone, icon, title, desc, children, useNew }) => {
    if (!useNew) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-700 mb-1">{title}</h2>
                <p className="text-sm text-gray-400 mb-4">{desc}</p>
                {children}
            </div>
        );
    }
    const grad =
        tone === 'accent' ? 'from-accent to-accent-hover'
            : tone === 'warning' ? 'from-warning to-warning'
                : 'from-info to-info';
    return (
        <div className="bg-surface border border-border-subtle rounded-lg overflow-hidden shadow-sm">
            <div className={`bg-gradient-to-r ${grad} px-5 py-3 flex items-center gap-2`}>
                {icon}
                <h2 className="text-sm font-semibold text-white">{title}</h2>
            </div>
            <div className="p-5">
                <p className="text-sm text-ink-secondary mb-3">{desc}</p>
                {children}
            </div>
        </div>
    );
};

interface FeatureFlagsPanelProps {
    settings: SettingsMap;
    isSuperadmin: boolean;
    useNew: boolean;
    onSave: (key: string, value: string) => Promise<void>;
}

function FeatureFlagsPanel({ settings, isSuperadmin, useNew, onSave }: FeatureFlagsPanelProps) {
    return (
        <div className="space-y-6">
            <FeatureSection
                useNew={useNew}
                tone="accent"
                icon={<FiUsers className="text-white text-lg" />}
                title="User Management"
                desc="Controls for operator-facing user management screens."
            >
                <SettingCard label="Password Reset Screen" settingKey="PASSWORD_RESET_ENABLED" settings={settings} isSuperadmin={isSuperadmin} type="toggle" onSave={onSave} />
            </FeatureSection>

            <FeatureSection
                useNew={useNew}
                tone="warning"
                icon={<FiTrash2 className="text-white text-lg" />}
                title="Infra User Cleanup"
                desc={<>Automated lifecycle for expired accounts: graduated alerts → home deletion → backup deletion. <span className={useNew ? 'text-warning font-medium' : 'text-amber-600 font-medium'}>Turning this ON will immediately activate the nightly cron job.</span></>}
            >
                <SettingCard
                    label="Enable Cleanup Lifecycle" settingKey="CLEANUP_ENABLED" settings={settings} isSuperadmin={isSuperadmin} type="toggle"
                    disabledReason="GUI control is locked. Enable via database: UPDATE system_settings SET value='true' WHERE `key`='CLEANUP_ENABLED';"
                    onSave={onSave}
                />
                <SettingCard label="Grace Period After Expiry" settingKey="CLEANUP_GRACE_PERIOD_DAYS" settings={settings} isSuperadmin={isSuperadmin} type="number" unit="days" onSave={onSave} />
                <SettingCard label="Backup Retention After Home Deletion" settingKey="CLEANUP_BACKUP_RETENTION_DAYS" settings={settings} isSuperadmin={isSuperadmin} type="number" unit="days" onSave={onSave} />
                <SettingCard label="Backup Base Path (per server)" settingKey="CLEANUP_BACKUP_BASE_PATH" settings={settings} isSuperadmin={isSuperadmin} type="text" onSave={onSave} />
            </FeatureSection>

            <FeatureSection
                useNew={useNew}
                tone="info"
                icon={<FiLayout className="text-white text-lg" />}
                title="GUI Redesign Rollout"
                desc="Master switch for the redesigned console (docs/design/). When off, every user sees the current GUI regardless of their personal preference. When on, users choose old vs. new in their own Preferences."
            >
                <SettingCard label="Enable New GUI" settingKey="NEW_GUI_ENABLED" settings={settings} isSuperadmin={isSuperadmin} type="toggle" onSave={onSave} />
            </FeatureSection>

            <FeatureSection
                useNew={useNew}
                tone="accent"
                icon={<FiBox className="text-white text-lg" />}
                title="Workspace Container"
                desc="k3s / IWM workspace features: admin Definitions / Groups / NFS / Sessions, and the participant myws portal."
            >
                <SettingCard label="Enable Workspace Container" settingKey="WORKSPACE_CONTAINER_ENABLED" settings={settings} isSuperadmin={isSuperadmin} type="toggle" onSave={onSave} />
                <SettingCard label="Enable MyWS Portal" settingKey="MYWS_PORTAL_ENABLED" settings={settings} isSuperadmin={isSuperadmin} type="toggle" onSave={onSave} />
            </FeatureSection>

            <div className={useNew
                ? 'bg-surface border border-dashed border-border-subtle rounded-lg p-5 opacity-60'
                : 'bg-white rounded-lg shadow-sm p-6 opacity-50'}>
                <h2 className={useNew ? 'text-sm font-semibold text-ink-secondary mb-1' : 'text-lg font-semibold text-gray-400 mb-1'}>More Features</h2>
                <p className={useNew ? 'text-sm text-ink-secondary' : 'text-sm text-gray-400'}>Additional feature toggles will appear here as they are implemented.</p>
            </div>
        </div>
    );
}

export default function SystemSettingsPage() {
    const { user } = useAuthGuard();
    const { isSidebarOpen } = useSidebar();
    const useNew = useNewGui();
    const isSuperadmin = user?.role === 'superadmin';

    const [settings, setSettings] = useState<SettingsMap>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('features');

    const fetchSettings = useCallback(async () => {
        try {
            const { data } = await api.get<SettingsMap>('/api/system-settings');
            setSettings(data);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleSave = useCallback(async (key: string, value: string) => {
        setSaveError(null);
        setSaveSuccess(null);
        try {
            const { data } = await api.put<Setting>(`/api/system-settings/${key}`, { value });
            setSettings(prev => ({ ...prev, [key]: data }));
            invalidateSettingsCache();
            setSaveSuccess(`Setting "${key}" updated successfully.`);
            setTimeout(() => setSaveSuccess(null), 3000);
        } catch (err: any) {
            setSaveError(err?.response?.data?.message || `Failed to update ${key}`);
        }
    }, []);

    if (!user || !['itadmin', 'superadmin'].includes(user.role)) {
        return (
            <div className={`flex-1 p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
                <Alert type="error" message="Access denied. Superadmin role required." showIcon />
            </div>
        );
    }

    const shellClass = useNew
        ? `flex-1 p-6 bg-surface-app font-plex transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`
        : `flex-1 p-6 bg-gray-50 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`;

    const tabItems = [
        {
            key: 'features',
            label: 'Feature flags',
            children: loading ? (
                <Spin tip="Loading settings..." />
            ) : (
                <FeatureFlagsPanel
                    settings={settings}
                    isSuperadmin={isSuperadmin}
                    useNew={useNew}
                    onSave={handleSave}
                />
            ),
        },
        {
            key: 'smtp',
            label: 'SMTP',
            children: <SmtpSettingsTab isSuperadmin={isSuperadmin} />,
        },
        {
            key: 'ipa',
            label: 'FreeIPA',
            children: <FreeIpaSettingsTab isSuperadmin={isSuperadmin} />,
        },
    ];

    return (
        <div className={shellClass}>
            {useNew ? (
                <PageHeader
                    title="Configuration"
                    purpose={isSuperadmin
                        ? 'Feature toggles, SMTP, and other system-wide settings.'
                        : 'Read-only feature flags — SMTP and writes require superadmin.'}
                />
            ) : (
                <>
                    <h1 className="text-3xl font-semibold text-gray-800 mb-2">Configuration</h1>
                    <p className="text-sm text-gray-500 mb-6">
                        Feature toggles, SMTP, and other system-wide settings.
                        {!isSuperadmin && (
                            <span className="text-amber-600 ml-2">
                                You have read-only access to feature flags. Superadmin required for SMTP and changes.
                            </span>
                        )}
                    </p>
                </>
            )}

            {error && <Alert type="error" message={error} showIcon className="mb-4" />}
            {saveError && <Alert type="error" message={saveError} showIcon closable onClose={() => setSaveError(null)} className="mb-4" />}
            {saveSuccess && <Alert type="success" message={saveSuccess} showIcon className="mb-4" />}

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                type={useNew ? 'line' : 'card'}
            />
        </div>
    );
}
